import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Phone, MessageCircle, User as UserIcon, AlertTriangle, CheckCircle, Calendar, Clock } from 'lucide-react';
import MentorLayout from '../../components/mentor/MentorLayout';
import CallMarkingForm from '../../components/mentor/CallMarkingForm';
import ImpactSummary from '../../components/mentor/ImpactSummary';
import axios from 'axios';


import { BACKEND_URL } from '../../config/api';
const FollowUpTasks = ({ user, onLogout }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPriority, setFilterPriority] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [showCallForm, setShowCallForm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    fetchTasks();
  }, [filterPriority, filterStatus]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('lms_token');
      let url = `${BACKEND_URL}/api/mentor/tasks/weekly?`;
      
      if (filterPriority) {
        url += `priority=${filterPriority}&`;
      }
      if (filterStatus) {
        url += `status=${filterStatus}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (taskId) => {
    try {
      const token = localStorage.getItem('lms_token');
      await axios.put(
        `${BACKEND_URL}/api/mentor/tasks/${taskId}/complete`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTasks();
    } catch (error) {
      console.error('Error marking task complete:', error);
    }
  };

  const handleCallClick = (task) => {
    setSelectedStudent({
      id: task.student_id,
      name: task.student_name,
      photo: task.student_photo
    });
    setShowCallForm(true);
  };

  const handleCallSubmit = async (formData) => {
    try {
      const token = localStorage.getItem('lms_token');
      await axios.post(
        `${BACKEND_URL}/api/mentor/followup/create`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShowCallForm(false);
      setSelectedStudent(null);
      fetchTasks(); // Refresh tasks
    } catch (error) {
      console.error('Error creating follow-up:', error);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-red-100 text-red-800 border-red-400',
      high: 'bg-orange-100 text-orange-800 border-orange-400',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-400',
      low: 'bg-blue-100 text-blue-800 border-blue-400'
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityIcon = (priority) => {
    if (priority === 'critical') return '🔴';
    if (priority === 'high') return '🟠';
    if (priority === 'medium') return '🟡';
    return '🔵';
  };

  const getRiskLabel = (risk) => {
    const labels = {
      inactive_7days: 'Inactive >7d',
      zero_progress: 'Zero Progress',
      failed_quizzes: 'Failed Quizzes',
      fees_pending: 'Fees Pending',
      low_engagement: 'Low Engagement'
    };
    return labels[risk] || risk;
  };

  if (loading) {
    return (
      <MentorLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading tasks...</p>
          </div>
        </div>
      </MentorLayout>
    );
  }

  return (
    <MentorLayout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Follow-up Tasks</h1>
          <p className="text-gray-600 mt-1">Weekly call tasks auto-generated based on student activity</p>
        </div>

        {/* Impact Summary Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-1 h-6 bg-blue-600 rounded"></span>
            Impact Checker Dashboard
          </h2>
          <ImpactSummary />
        </div>

        {/* Filters */}
        <div className="mb-6 bg-white p-4 rounded-lg shadow-sm">
          <div className="flex flex-wrap gap-4">
            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Priorities</option>
                <option value="critical">🔴 Critical</option>
                <option value="high">🟠 High</option>
                <option value="medium">🟡 Medium</option>
                <option value="low">🔵 Low</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Tasks</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Task Count */}
            <div className="flex items-end">
              <div className="px-4 py-2 bg-blue-50 text-blue-800 rounded-lg font-medium">
                {tasks.length} task{tasks.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {/* Tasks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tasks.map((task) => (
            <Card key={task.task_id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                {/* Priority Badge */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${getPriorityColor(task.priority)}`}>
                    {getPriorityIcon(task.priority)} {task.priority.toUpperCase()}
                  </span>
                  {task.status === 'completed' && (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  )}
                </div>

                {/* Student Info */}
                <div className="flex items-center gap-3 mb-4">
                  {task.student_photo ? (
                    <img
                      src={task.student_photo}
                      alt={task.student_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                      <UserIcon className="w-6 h-6 text-blue-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900">{task.student_name}</h3>
                    <p className="text-xs text-gray-600">Student ID: {task.student_id.slice(0, 8)}...</p>
                  </div>
                </div>

                {/* Risk Reasons */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-gray-700 mb-2">Risk Reasons:</p>
                  <div className="flex flex-wrap gap-1">
                    {task.risk_reason.map((risk, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full border border-red-300"
                      >
                        {getRiskLabel(risk)}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Task Details */}
                <div className="space-y-2 text-sm mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
                  </div>
                  {task.last_followup && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>Last call: {new Date(task.last_followup).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                {task.status === 'pending' ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleCallClick(task)}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Call
                    </button>
                    <button className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors">
                      <MessageCircle className="w-4 h-4" />
                      Message
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 px-3 py-2 bg-green-100 text-green-800 rounded-lg text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Completed
                  </div>
                )}

                {task.status === 'pending' && (
                  <button
                    onClick={() => handleMarkComplete(task.task_id)}
                    className="w-full mt-2 flex items-center justify-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium transition-colors"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Mark Complete
                  </button>
                )}
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {tasks.length === 0 && (
          <div className="text-center py-12">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">All tasks completed!</p>
            <p className="text-sm text-gray-600 mt-2">No pending follow-up tasks at the moment.</p>
          </div>
        )}

        {/* Call Marking Form Modal */}
        {showCallForm && selectedStudent && (
          <CallMarkingForm
            student={selectedStudent}
            onClose={() => {
              setShowCallForm(false);
              setSelectedStudent(null);
            }}
            onSubmit={handleCallSubmit}
          />
        )}
      </div>
    </MentorLayout>
  );
};

export default FollowUpTasks;
